import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useCallback, useEffect } from "react";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { useGetSubscriptionsQuery } from "@/redux/api/userApi";

interface TeamPlusButtonProps {
  seats: number;
  setSeats: (count: number) => void;
  subscriptionType: string;
}

export const TeamPlusButton = ({
  seats = 1,
  setSeats,
  subscriptionType,
}: TeamPlusButtonProps) => {
  const { t } = useTranslation("common");

  const { data: userSubscription, loading } = useGetSubscriptionsQuery(
    {},
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );
  // console.log('userSubscription?.plan: ', userSubscription?.plan)

  const isShowTeamSeatsInfo =
    userSubscription?.plan !== "none" &&
    subscriptionType !== "block" &&
    ((subscriptionType === "upgrade" &&
      !userSubscription?.plan?.toLowerCase()?.includes("personal")) ||
      userSubscription?.plan?.toLowerCase()?.includes("team"));

  const isShowTeamSeatManage =
    (userSubscription?.plan === "none" || subscriptionType === "upgrade") &&
    !userSubscription?.plan?.toLowerCase()?.includes("team");

  const handleDecrease = useCallback(() => {
    const userSeats = userSubscription?.seats ?? 1;
    if (userSeats >= seats) return;
    if (seats > 1) setSeats(seats - 1);
  }, [seats, setSeats, userSubscription?.seats]);

  const handleIncrease = useCallback(() => {
    setSeats(seats + 1);
  }, [seats, setSeats]);

  useEffect(() => {
    if (userSubscription?.seats) setSeats(userSubscription?.seats ?? 1);
  }, [setSeats, userSubscription?.seats]);

  useEffect(() => {
    return () => {
      setSeats(1);
    };
  }, [setSeats]);

  return (
    <>
      {isShowTeamSeatManage && (
        <StyledButtonContainer>
          <StyledInputButton isLeft onClick={handleDecrease}>
            <MinusOutlined />
          </StyledInputButton>
          <SeatsDisplay>
            {seats} {t("seat")}
          </SeatsDisplay>
          <StyledInputButton isRight onClick={handleIncrease}>
            <PlusOutlined />
          </StyledInputButton>
        </StyledButtonContainer>
      )}
      {isShowTeamSeatsInfo && (
        <StyledSeatsContainer>
          {t("Your team currently has {{seat}} seats", {
            seat: userSubscription?.seats || 1,
          })}
        </StyledSeatsContainer>
      )}
      {!isShowTeamSeatsInfo && <WhitespaceDiv />}
    </>
  );
};

const SeatsDisplay = styled.p`
  margin: 0;
  font-size: 14px;
  color: #333;
  text-align: center;
  flex: 1;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 5px;
  height: 28px;
  width: 226px;
  margin-top: 10px;
`;

interface StyledInputButtonProps {
  isLeft?: boolean;
  isRight?: boolean;
}

const StyledInputButton = styled(Button)<StyledInputButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  aspect-ratio: 1;
  color: #808080;
  border: none;
  background-color: #f4f4f4;
  border-radius: ${({ isLeft, isRight }) => {
    if (isLeft) return "5px 0 0 5px";
    if (isRight) return "0 5px 5px 0";
    return "0";
  }};
`;

const StyledSeatsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #4276ee1a;
  color: #4276ee;
  border-radius: 5px;
  height: 28px;
  width: 226px;
  margin-top: 10px;
`;

const WhitespaceDiv = styled.div`
  height: 38px;
`;
